/**
 * Validação de referências externas em ações tipo 4 (Alterar a Solicitação).
 */

import { acoesFromSlot, acoesToPayload, legacyFieldsFromAcoes } from "./chatbotSlotActions";
import {
  ENTITY_OBJECT_MAP,
  REF_KIND_AGENT,
  REF_KIND_CHANNEL,
  REF_KIND_DELIVERY,
  REF_KIND_PAYMENT,
  REF_KIND_POSITION,
  REF_KIND_SUBTYPE,
  REF_KIND_TYPE,
  REF_KIND_USER,
} from "./chatbotFlowReferences";

const STATIC_ENTITY_OBJECTS = new Set(["statuslead"]);

function normalizeIdList(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value.map((v) => String(v)).filter((v) => v !== "");
  return [String(value)];
}

function formFieldEntries(parameterForm) {
  if (!parameterForm || typeof parameterForm !== "object") return [];
  const root = parameterForm.acoes || parameterForm.fields || parameterForm.condicoes || parameterForm;
  if (!root || typeof root !== "object") return [];
  return Object.entries(root);
}

export function parseAuditEntityIndex(raw) {
  const data = raw?.data ?? raw ?? {};
  const toSet = (key) =>
    new Set((Array.isArray(data[key]) ? data[key] : []).map((id) => Number(id)).filter(Number.isFinite));
  const toStringSet = (key) =>
    new Set(
      (Array.isArray(data[key]) ? data[key] : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    );
  return {
    [REF_KIND_USER]: toSet("user"),
    [REF_KIND_AGENT]: toStringSet("agent"),
    [REF_KIND_CHANNEL]: toSet("channel"),
    [REF_KIND_TYPE]: toSet("type"),
    [REF_KIND_SUBTYPE]: toSet("subtype"),
    [REF_KIND_PAYMENT]: toSet("paymentMethod"),
    [REF_KIND_DELIVERY]: toSet("deliveryMethod"),
    [REF_KIND_POSITION]: toSet("position"),
  };
}

export function buildClearLeadRefFix(slot, actionIndex, fieldName) {
  const acoes = acoesFromSlot(slot);
  const newAcoes = acoes.map((a, i) => {
    if (i !== actionIndex) return a;
    const values = { ...(a.values || {}) };
    const labels = { ...(a.labels || {}) };
    delete values[fieldName];
    delete labels[fieldName];
    return { ...a, values, labels };
  });

  return {
    kind: "update_slot",
    payload: {
      id: Number(slot.id),
      acoes: acoesToPayload(newAcoes),
      ...legacyFieldsFromAcoes(newAcoes),
    },
  };
}

/**
 * @returns {Array<{ field, refKind, refId, label, actionIndex, fix }>}
 */
export function collectBrokenLeadActionRefs(slot, parameterForm, entityIndex) {
  if (!parameterForm || !entityIndex) return [];

  const out = [];
  const acoes = acoesFromSlot(slot);

  acoes.forEach((action, actionIndex) => {
    if (Number(action.tipo_acao) !== 4 || Number(action.id_acao) !== 4) return;

    for (const [fieldName, fieldDef] of formFieldEntries(parameterForm)) {
      const objectType = fieldDef?.object;
      if (!objectType || STATIC_ENTITY_OBJECTS.has(objectType)) continue;

      const refKind = ENTITY_OBJECT_MAP[objectType];
      if (!refKind) continue;

      const validIds = entityIndex[refKind];
      if (!validIds || !(validIds instanceof Set) || validIds.size === 0) continue;

      const raw = action.values?.[fieldName];
      for (const id of normalizeIdList(raw)) {
        if (refKind === REF_KIND_AGENT) {
          const refId = String(id || "").trim();
          if (!refId || validIds.has(refId)) continue;

          const label =
            action.labels?.[fieldName] ||
            fieldDef?.label ||
            refId;

          out.push({
            field: fieldName,
            refKind,
            refId,
            label: String(label),
            actionIndex,
            fix: buildClearLeadRefFix(slot, actionIndex, fieldName),
          });
          continue;
        }

        const refId = Number(id);
        if (!refId || validIds.has(refId)) continue;

        const label =
          action.labels?.[fieldName] ||
          fieldDef?.label ||
          `#${refId}`;

        out.push({
          field: fieldName,
          refKind,
          refId,
          label: String(label),
          actionIndex,
          fix: buildClearLeadRefFix(slot, actionIndex, fieldName),
        });
      }
    }
  });

  return out;
}
